'use client';

import React from 'react';

import { Box, FormControl, FormErrorMessage, FormLabel, Stack, useTheme } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import 'react-international-phone/style.css';
import { PhoneInput } from 'react-international-phone';

import { LoadingButton } from '@components/custom-buttons';
import {
    FormControlTextField,
    FormControlFileField,
    FormControlSelectField,
} from '@components/custom-form-control';
import { CustomText } from '@components/Custom-text';
import { UserType } from '@enums/index';

import { MenteeRegisterSchema, MentorRegisterSchema } from '../../schema';
import { SignUpRequest } from '../../types';

interface SignUpFormProps {
    onSubmit: (values: SignUpRequest) => void | Promise<any>;
    isLoading?: boolean;
    userType: UserType;
    domainData: { key: string; value: string }[];
}

const initialValues = {
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    password: '',
    cv: null,
    domainId: '',
    timezone: '',
};

const SignUpForm = ({ onSubmit, isLoading, userType, domainData }: SignUpFormProps) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [phoneInteracted, setPhoneInteracted] = React.useState(false);

    const theme = useTheme();

    const handleShowPassword = () => {
        setShowPassword(prev => !prev);
    };

    const handleSubmit = async (values: SignUpRequest) => {
        const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        onSubmit({
            ...values,
            phone: `${values.phone}`,
            timezone: currentTimeZone ?? 'Asia/Kolkata',
        });
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={
                userType === UserType.Mentor ? MentorRegisterSchema : MenteeRegisterSchema
            }
            onSubmit={handleSubmit}
        >
            {({ isSubmitting, values, setFieldValue, errors, submitCount }) => (
                <Form>
                    <Stack width="100%" spacing={4}>
                        <FormControlTextField
                            name="name"
                            placeholder="Enter your full name"
                            label="Full name"
                            type="text"
                            isRequired
                        />
                        <FormControlTextField
                            name="email"
                            placeholder="Enter your email"
                            label="Email"
                            type="email"
                            isRequired
                        />
                        <Box>
                            <FormControl
                                isInvalid={!!errors.phone && (phoneInteracted || submitCount > 0)}
                            >
                                <FormLabel
                                    htmlFor="phone"
                                    mb={1}
                                    fontWeight="semibold"
                                    fontSize="sm"
                                    color="titleText"
                                >
                                    Phone number{' '}
                                    <CustomText as="span" size="lg" color="red">
                                        *
                                    </CustomText>
                                </FormLabel>
                                <PhoneInput
                                    defaultCountry="in"
                                    value={
                                        values.phone ? `+${values.countryCode}${values.phone}` : ''
                                    }
                                    onChange={(phone, metadata) => {
                                        const countryCode = metadata.country.dialCode;
                                        const nationalNumber = phone?.startsWith(`+${countryCode}`)
                                            ? phone.replace(`+${countryCode}`, '')
                                            : phone?.replace(/^\+/, '') || '';

                                        setFieldValue('phone', nationalNumber);
                                        setFieldValue('countryCode', countryCode);
                                    }}
                                    onBlur={() => setPhoneInteracted(true)}
                                    forceDialCode
                                    inputStyle={{
                                        width: '100%',
                                        height: '40px',
                                        borderTopRightRadius: '0.375rem',
                                        borderBottomRightRadius: '0.375rem',
                                        borderColor:
                                            errors.phone && (phoneInteracted || submitCount > 0)
                                                ? theme.colors.red[500]
                                                : theme.colors.gray[200],
                                        borderWidth:
                                            errors.phone && (phoneInteracted || submitCount > 0)
                                                ? '2px'
                                                : '1px',

                                        paddingLeft: '10px',
                                        fontSize: 'sm',
                                    }}
                                />
                                {errors.phone && (phoneInteracted || submitCount > 0) && (
                                    <FormErrorMessage fontSize="xs" mt={1}>
                                        {errors.phone}
                                    </FormErrorMessage>
                                )}
                            </FormControl>
                        </Box>

                        <FormControlTextField
                            name="password"
                            placeholder="Enter your password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            isRequired
                            showRightElement
                            onRightElementClick={handleShowPassword}
                        />
                        {userType === UserType.Mentor ? (
                            <FormControlFileField
                                name="cv"
                                placeholder="Choose file to upload"
                                label="Upload CV"
                                isRequired
                            />
                        ) : null}
                        <FormControlSelectField
                            name="domainId"
                            placeholder="Select Domain"
                            label="Domain"
                            options={domainData}
                            isRequired
                        />
                    </Stack>
                    <Box mt={8} width="100%">
                        <LoadingButton
                            buttonLabel={`Signup as ${
                                userType === UserType.Mentor ? 'Mentor' : 'Mentee'
                            }`}
                            type="submit"
                            isLoading={isSubmitting || isLoading!}
                            loadingText="Registering"
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};

export default SignUpForm;
